import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, LogIn, RefreshCw } from 'lucide-react';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
  resetting: boolean;
}

async function clearApplicationCache() {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  }

  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(
      keys
        .filter((key) => key.startsWith('sihterica-'))
        .map((key) => caches.delete(key))
    );
  }
}

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { error: null, resetting: false };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error, resetting: false };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('Šihterica se nije mogla prikazati.', error, info);
  }

  private resetAndReload = async () => {
    this.setState({ resetting: true });
    try {
      await clearApplicationCache();
    } finally {
      window.location.reload();
    }
  };

  private openLogin = async () => {
    this.setState({ resetting: true });
    try {
      sessionStorage.removeItem('sihterica_tenant_access');
      sessionStorage.removeItem('sihterica_superadmin_access');
      await clearApplicationCache();
    } finally {
      window.location.replace('/login');
    }
  };

  render() {
    if (!this.state.error) return this.props.children;

    return (
      <main className="fatal-screen" role="alert">
        <section className="fatal-screen__card">
          <span className="fatal-screen__icon"><AlertTriangle /></span>
          <span className="eyebrow">SIGURAN OPORAVAK APLIKACIJE</span>
          <h1>Aplikacija se nije uspjela učitati</h1>
          <p>
            Najčešći uzrok je stara spremljena verzija nakon nadogradnje. Osvježavanje
            će očistiti samo cache Šihterice i ponovno učitati aktualnu verziju.
          </p>
          <div className="fatal-screen__actions">
            <button
              className="button button--primary"
              type="button"
              disabled={this.state.resetting}
              onClick={() => void this.resetAndReload()}
            >
              <RefreshCw /> {this.state.resetting ? 'Osvježavam…' : 'Očisti cache i osvježi'}
            </button>
            <button
              className="button button--secondary"
              type="button"
              disabled={this.state.resetting}
              onClick={() => void this.openLogin()}
            >
              <LogIn /> Vrati me na prijavu
            </button>
          </div>
          {import.meta.env.DEV && (
            <details className="fatal-screen__details">
              <summary>Tehnički detalj</summary>
              <code>{this.state.error.message}</code>
            </details>
          )}
        </section>
      </main>
    );
  }
}
